/**
 * @fileoverview In-memory store behind the capabilities mock endpoints.
 * @description Stands in for the permissions and role_permissions tables: the seeded catalogue
 * (capabilities-seed.json, taken from the Capabilities design) plus the grants and revokes Super
 * Admin has made on top of it. Held in a module-level map for the browser session — never
 * localStorage, like the other features' mock stores — so a reload starts from the seed again.
 * Serves the role, permission and role-permission lists in the real endpoints' item shapes (with
 * the saved changes applied), and keeps a row version per role that advances whenever a save
 * changes one of its grants, so a stale `ifMatch` can be refused like the real backend does.
 * capabilities-mock-update.ts validates a bulk update against it.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import seed from './capabilities-seed.json'

import type {
  PermissionDto,
  RoleDto,
  RolePermissionDto,
} from '@/features/capabilities/types/role-capabilities-api.types'

type GrantStatus = 'ACTIVE' | 'REVOKED'

interface SeedPermission {
  permissionCode: string
  permissionName: string
  description: string
  status: string
  isSensitive: boolean
  requiresStepUpMfa: boolean
}

interface SeedRolePermission {
  roleCode: string
  permissionCode: string
  status: string
}

const SUPER_ADMIN_CODE = 'SUPER_ADMIN'
const SEED_PERMISSIONS = seed.permissions as SeedPermission[]
const SEED_GRANTS = seed.rolePermissions as SeedRolePermission[]
const SALES_ROLES = seed.roles.filter((role) => role.module === 'SALES')

const baseGrants = new Map<string, GrantStatus>(
  SEED_GRANTS.map((grant) => [
    `${grant.roleCode}|${grant.permissionCode}`,
    grant.status === 'ACTIVE' ? 'ACTIVE' : 'REVOKED',
  ]),
)

// Super Admin's grants and revokes on top of the seed. A change back to the seeded value is dropped,
// so `size` is the number of pairs that differ from the seed.
const overrides = new Map<string, GrantStatus>()

// How many times each role's grants have changed since load, keyed by role code. The role's row
// version and permissionVersion are derived from it.
const roleChanges = new Map<string, number>()
const BASE_ROW_VERSION = 2001
const BYTES_IN_ROW_VERSION = 8

/**
 * @description Whether a role currently holds a permission: Super Admin holds every one, anyone else
 * by an override or else the seed.
 * @param {string} roleCode - The role code.
 * @param {string} permissionCode - The permission code.
 * @returns {boolean} True when the role holds the permission.
 */
export function isHeld(roleCode: string, permissionCode: string): boolean {
  if (roleCode === SUPER_ADMIN_CODE) {
    return true
  }

  const key = `${roleCode}|${permissionCode}`
  return (overrides.get(key) ?? baseGrants.get(key)) === 'ACTIVE'
}

/**
 * @description The mock role id for a role code. The real ids are UUIDs; the mock only needs them
 * to be stable and reversible.
 * @param {string} roleCode - The role code.
 * @returns {string} The role id.
 */
function roleIdOf(roleCode: string): string {
  return `mock-role-${roleCode}`
}

/**
 * @description A role's row version as the real endpoints send it: eight bytes, big-endian, in
 * base64 — advanced by one for every save that changed one of the role's grants.
 * @param {string} roleCode - The role code.
 * @returns {string} The row version.
 */
export function roleRowVersion(roleCode: string): string {
  const bytes = new DataView(new ArrayBuffer(BYTES_IN_ROW_VERSION))
  bytes.setBigUint64(0, BigInt(BASE_ROW_VERSION + (roleChanges.get(roleCode) ?? 0)))
  return btoa(String.fromCharCode(...new Uint8Array(bytes.buffer)))
}

/**
 * @description A role's permissionVersion: 1 at load, plus one per save that changed its grants.
 * @param {string} roleCode - The role code.
 * @returns {number} The version.
 */
export function rolePermissionVersion(roleCode: string): number {
  return 1 + (roleChanges.get(roleCode) ?? 0)
}

/**
 * @description The Sales role with this mock id, if any.
 * @param {string} roleId - The mock role id.
 * @returns {{ roleCode: string; roleName: string } | undefined} The role, or undefined.
 */
export function findSalesRole(roleId: string) {
  return SALES_ROLES.find((role) => roleIdOf(role.roleCode) === roleId)
}

/**
 * @description The seeded permission with this mock id, if any.
 * @param {string} permissionId - The mock permission id.
 * @returns {SeedPermission | undefined} The permission, or undefined.
 */
export function findPermission(permissionId: string): SeedPermission | undefined {
  return SEED_PERMISSIONS.find((item) => toPermissionDto(item).permissionId === permissionId)
}

/**
 * @description Sets one grant, dropping the override when it returns to the seeded value, and
 * reports whether that changed anything.
 * @param {string} roleCode - The role code.
 * @param {string} permissionCode - The permission code.
 * @param {boolean} granted - Whether the role should hold the permission.
 * @returns {boolean} True when the grant changed.
 */
export function setGrant(roleCode: string, permissionCode: string, granted: boolean): boolean {
  if (isHeld(roleCode, permissionCode) === granted) {
    return false
  }

  const key = `${roleCode}|${permissionCode}`
  if (granted === (baseGrants.get(key) === 'ACTIVE')) {
    overrides.delete(key)
  } else {
    overrides.set(key, granted ? 'ACTIVE' : 'REVOKED')
  }
  return true
}

/**
 * @description Advances a role's row version, as the backend does once per request that changed it.
 * @param {string} roleCode - The role code.
 */
export function bumpRoleVersion(roleCode: string): void {
  roleChanges.set(roleCode, (roleChanges.get(roleCode) ?? 0) + 1)
}

/**
 * @description The mock id of a permission, as the list endpoints return it.
 * @param {string} permissionCode - The permission code.
 * @returns {string} The permission id.
 */
export function permissionIdOf(permissionCode: string): string {
  return `mock-permission-${permissionCode}`
}

/**
 * @description Builds one permission as the List Permissions endpoint returns it.
 * @param {SeedPermission} permission - The seeded permission.
 * @returns {PermissionDto} The response item.
 */
function toPermissionDto(permission: SeedPermission): PermissionDto {
  const segments = permission.permissionCode.split('.')
  return {
    permissionId: permissionIdOf(permission.permissionCode),
    permissionCode: permission.permissionCode,
    permissionName: permission.permissionName,
    description: permission.description,
    resourceCode: segments.slice(0, -1).join('.'),
    actionCode: segments[segments.length - 1] ?? '',
    status: permission.status,
    isSensitive: permission.isSensitive,
    requiresStepUpMfa: permission.requiresStepUpMfa,
  }
}

/**
 * @description The Sales roles as the List Roles endpoint returns them (all ACTIVE).
 * @returns {RoleDto[]} The roles, Super Admin first.
 */
export function listRoleDtos(): RoleDto[] {
  return SALES_ROLES.map((role) => ({
    roleId: roleIdOf(role.roleCode),
    roleCode: role.roleCode,
    roleName: role.roleName,
    description: null,
    roleCategory: 'INTERNAL',
    roleOrigin: 'SYSTEM',
    status: 'ACTIVE',
    isProtected: role.roleCode === SUPER_ADMIN_CODE,
    permissionVersion: rolePermissionVersion(role.roleCode),
    rowVersion: roleRowVersion(role.roleCode),
  }))
}

/**
 * @description The permission catalogue, optionally narrowed to one status, as the List
 * Permissions endpoint returns it (ordered by code).
 * @param {string} status - ACTIVE or INACTIVE.
 * @returns {PermissionDto[]} The matching permissions.
 */
export function listPermissionDtos(status: string): PermissionDto[] {
  return SEED_PERMISSIONS.filter((permission) => permission.status === status)
    .map(toPermissionDto)
    .sort((a, b) => a.permissionCode.localeCompare(b.permissionCode))
}

/**
 * @description A role's permission mappings as the List Role Permissions endpoint returns them: the
 * permissions it currently holds (ACTIVE) or has had revoked (REVOKED), with the seed plus Super
 * Admin's saved changes applied.
 * @param {string} roleId - The mock role id.
 * @param {string} status - ACTIVE or REVOKED.
 * @returns {RolePermissionDto[] | undefined} The mappings, or undefined for an unknown role.
 */
export function listRolePermissionDtos(
  roleId: string,
  status: string,
): RolePermissionDto[] | undefined {
  const role = SALES_ROLES.find((item) => roleIdOf(item.roleCode) === roleId)
  if (!role) {
    return undefined
  }

  const wanted = status === 'ACTIVE'
  return SEED_PERMISSIONS.filter(
    (permission) => isHeld(role.roleCode, permission.permissionCode) === wanted,
  )
    .map((permission) => ({
      rolePermissionId: `mock-mapping-${role.roleCode}-${permission.permissionCode}`,
      roleId,
      permission: toPermissionDto(permission),
      status,
      grantedAt: '2026-10-15T09:00:00.000Z',
      revokedAt: wanted ? null : '2026-10-28T09:00:00.000Z',
      rowVersion: 'AAAAAAAAB9E=',
    }))
    .sort((a, b) => a.permission.permissionCode.localeCompare(b.permission.permissionCode))
}

/**
 * @description Drops every override and version advance, putting each role back to the seed.
 */
export function resetOverrides(): void {
  overrides.clear()
  roleChanges.clear()
}
